import { useQuery } from '@apollo/client';
import type { FC } from 'react';

import { EntityPageContractType } from '@xing-com/crate-common-graphql-types';
import {
  MetaData,
  useEditContext,
  usePageContext,
} from '@xing-com/crate-entity-pages-common';

import * as Styled from './app.styles';
import { AboutUsErrorPage } from './components/about-us-error-page/about-us-error-page';
import { Affiliates } from './components/affiliates-container/affiliates';
import { Awards } from './components/awards/awards';
import { DetailedDescription } from './components/detailed-description/detailed-description';
import { DocumentsContainer } from './components/documents/documents-container';
import { FactsContainer } from './components/facts/facts-container';
import { GalleryPreviewContainer } from './components/gallery-preview/gallery-preview-container';
import { AboutUsSkeleton } from './components/skeleton/skeleton';
import { DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE } from './config/media';
import { AboutUsSubpageDocument } from './graphql/queries/about-us-subpage-query.gql-types';

export const App: FC = () => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();

  const isFreePage = pageContext.contractType === EntityPageContractType.Free;

  const { data, loading, error, refetch } = useQuery(AboutUsSubpageDocument, {
    variables: {
      id: pageContext.pageSlug,
      first: DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  if (loading) {
    return <AboutUsSkeleton />;
  }

  if (error || !data) {
    return <AboutUsErrorPage refetch={refetch} />;
  }

  const companyData =
    data.facts?.__typename === 'CompanyAboutUsFacts'
      ? data.facts.companyData
      : null;
  const badges = companyData?.kununuData?.badges || [];
  const availableBadges = badges
    .filter((badge) => !!badge)
    .filter((badge) => badge?.image?.[0]?.url);

  const pagesAboutUs =
    data.pagesAboutUs?.__typename === 'AboutEntity' ? data.pagesAboutUs : null;
  const descriptionAboutArticle = pagesAboutUs?.aboutArticle;
  const aboutUsDocuments =
    pagesAboutUs?.documents?.__typename === 'EntityDocumentConnection'
      ? pagesAboutUs?.documents
      : null;
  const documentsEdges = aboutUsDocuments?.edges ?? [];
  const documents =
    documentsEdges.map((edge) => edge?.node)?.filter((node) => !!node) ?? [];

  const showAwards = availableBadges.length > 0;
  const showDocuments = documents.length > 0 || (isEditing && !isFreePage);
  const showAffiliates = !isFreePage || pageContext.isEditor;

  return (
    <div data-testid="ABOUT_US_SUBMODULE">
      <MetaData description={pagesAboutUs?.summary} />
      <Styled.TopContent>
        <DetailedDescription
          articleHeadline={descriptionAboutArticle?.header?.title?.text ?? ''}
          articleContent={descriptionAboutArticle?.content ?? ''}
        />
        {data.facts ? <FactsContainer facts={data.facts} /> : null}
      </Styled.TopContent>
      {pagesAboutUs?.media ? (
        <GalleryPreviewContainer
          media={pagesAboutUs.media}
          mediaItemsNumber={DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE}
        />
      ) : null}
      {showAwards ? <Awards badges={availableBadges} /> : null}
      {showDocuments ? <DocumentsContainer documents={documents} /> : null}
      {showAffiliates ? <Affiliates /> : null}
    </div>
  );
};
